import { Combobox, type ComboboxOptionsType } from '@zillow/constellation-10';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { LucideIcon } from '@/src/components/LucideIcon';
import { DEFAULT_POI_OPTION_VALUES, POI_OPTION_GROUPS, type POIOptionValue } from './types';
import { POI_ICON_GROUP_ICONS } from './poiCategoryIcons';
import { poiTypeComboboxContainer, poiTypeComboboxRoot } from './poiTypeCombobox.styles';

type PoiTypeComboboxProps = {
  selectedValues: Array<POIOptionValue>;
  onChange: (values: Array<POIOptionValue>) => void;
};

export function getDefaultPoiOptionValues(): Array<POIOptionValue> {
  return [...DEFAULT_POI_OPTION_VALUES];
}

const setPoiComboboxTestId = (element: HTMLDivElement | null) => {
  element?.querySelector('input')?.setAttribute('data-testid', 'combobox-points-of-interest');
};

export function PoiTypeCombobox({ selectedValues, onChange }: PoiTypeComboboxProps) {
  const t = useTranslations('hdp');

  const options = useMemo<ComboboxOptionsType>(
    () =>
      POI_OPTION_GROUPS.map((group) => {
        const groupLabel = t(group.labelKey);
        const groupIcon = group.options[0]?.iconGroup;
        const isFeaturedGroup = group.labelKey === 'poiGroupFeatured';

        return {
          heading:
            isFeaturedGroup || !groupIcon
              ? groupLabel
              : {
                  label: groupLabel,
                  icon: <LucideIcon icon={POI_ICON_GROUP_ICONS[groupIcon]} size="sm" />,
                },
          options: group.options.map((option) => ({
            value: option.value,
            label: option.labelKey ? t(option.labelKey) : option.label,
            meta: groupLabel,
            icon: option.isDefault ? <LucideIcon icon={POI_ICON_GROUP_ICONS[option.iconGroup]} size="sm" /> : undefined,
          })),
        };
      }),
    [t],
  );

  return (
    <div ref={setPoiComboboxTestId} className={poiTypeComboboxContainer}>
      <Combobox
        appearance="input"
        aria-label={t('pointsOfInterest')}
        css={poiTypeComboboxRoot}
        fluidDropdown
        limitChipsShown={5}
        options={options}
        placeholder={t('poiSearchPlaceholder')}
        showLabelForValue
        size="sm"
        value={selectedValues}
        onChange={(nextValue) => {
          if (Array.isArray(nextValue)) {
            onChange(nextValue);
          }
        }}
      />
    </div>
  );
}
